import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ProfitAndLoss.css';
import baseURL from '../../../../Url/NodeBaseURL';

const ProfitAndLoss = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [profitLossData, setProfitLossData] = useState(null);
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: ''
  });

  // Fetch Profit & Loss data
  const fetchProfitAndLoss = async () => {
    if (!dateRange.start_date || !dateRange.end_date) {
      alert('Please select both start and end dates');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${baseURL}/ledger/profit-loss`, {
        params: {
          start_date: dateRange.start_date,
          end_date: dateRange.end_date
        }
      });

      console.log('Profit & Loss Response:', response.data);

      if (response.data.success) {
        setProfitLossData(response.data);
      } else {
        setError(response.data.message || 'Failed to fetch Profit & Loss data');
      }
    } catch (err) {
      console.error('Error fetching Profit & Loss:', err);
      setError(err.response?.data?.error || 'Failed to fetch Profit & Loss data');
    } finally {
      setLoading(false);
    }
  };

  // Handle date change
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '₹0.00';
    return `₹${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Get financial year
  const getFinancialYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    if (month >= 3) {
      return `${year}-${year + 1}`;
    } else {
      return `${year - 1}-${year}`;
    }
  };

  // Set default date range to current financial year
  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    let startYear = year;
    if (month < 3) {
      startYear = year - 1;
    }

    setDateRange({
      start_date: `${startYear}-04-01`,
      end_date: today.toISOString().split('T')[0]
    });
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    if (dateRange.start_date && dateRange.end_date) {
      fetchProfitAndLoss();
    }
  }, []);

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="pl-container">
      <div className="pl-header">
        <div className="pl-header-top">
          <h2>📊 Profit & Loss Statement</h2>
          <div className="pl-header-actions">
            <button onClick={fetchProfitAndLoss} className="btn-refresh" disabled={loading}>
              🔄 Refresh
            </button>
            <button onClick={handlePrint} className="btn-print">
              🖨️ Print
            </button>
          </div>
        </div>
        <div className="pl-controls">
          <div className="date-range">
            <div className="date-group">
              <label>From:</label>
              <input
                type="date"
                name="start_date"
                value={dateRange.start_date}
                onChange={handleDateChange}
                className="date-input"
              />
            </div>
            <div className="date-group">
              <label>To:</label>
              <input
                type="date"
                name="end_date"
                value={dateRange.end_date}
                onChange={handleDateChange}
                className="date-input"
              />
            </div>
            <button
              onClick={fetchProfitAndLoss}
              className="btn-generate"
              disabled={loading}
            >
              {loading ? '⏳ Generating...' : '📈 Generate'}
            </button>
          </div>
          <div className="financial-year">
            <span className="fy-label">Financial Year:</span>
            <span className="fy-value">{getFinancialYear()}</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="pl-error">
          <span className="error-icon">❌</span>
          {error}
        </div>
      )}

      {loading ? (
        <div className="pl-loading">
          <div className="loader"></div>
          <p>Generating Profit & Loss statement...</p>
        </div>
      ) : profitLossData ? (
        <div className="pl-content" id="print-area">
          <div className="pl-report-header">
            <h3>Profit & Loss Statement</h3>
            <p className="pl-period">
              For the period from {new Date(profitLossData.period.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              to {new Date(profitLossData.period.end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
          </div>

          {/* ==================== 1. SALES REVENUE ==================== */}
          <div className="pl-section">
            <h4 className="section-title income-title">Sales Revenue</h4>
            <table className="pl-table">
              <tbody>
                <tr>
                  <td className="item-name">Sales Revenue</td>
                  <td className="item-amount">
                    {formatCurrency(profitLossData.sales_revenue || profitLossData.income?.total || 0)}
                  </td>
                </tr>
                <tr className="total-row">
                  <td className="total-label"><strong>Total Sales Revenue</strong></td>
                  <td className="total-amount">
                    <strong>{formatCurrency(profitLossData.sales_revenue || profitLossData.income?.total || 0)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ==================== 2. COST OF GOODS SOLD ==================== */}
          <div className="pl-section">
            <h4 className="section-title cogs-title">Cost of Goods Sold</h4>
            <table className="pl-table">
              <tbody>
                <tr>
                  <td className="item-name">
                    Opening Stock
                    {profitLossData.cogs_breakdown?.opening_stock_count > 0 && (
                      <span style={{ fontSize: '11px', color: '#888', marginLeft: '6px' }}>
                        ({profitLossData.cogs_breakdown.opening_stock_count} tags)
                      </span>
                    )}
                  </td>
                  <td className="item-amount">
                    {formatCurrency(profitLossData.cogs_breakdown?.opening_stock || 0)}
                  </td>
                </tr>
                <tr>
                  <td className="item-name">
                    + Purchases
                    {profitLossData.cogs_breakdown?.purchases_count > 0 && (
                      <span style={{ fontSize: '11px', color: '#888', marginLeft: '6px' }}>
                        ({profitLossData.cogs_breakdown.purchases_count} tags)
                      </span>
                    )}
                  </td>
                  <td className="item-amount">
                    {formatCurrency(profitLossData.cogs_breakdown?.purchases || 0)}
                  </td>
                </tr>
                <tr>
                  <td className="item-name">
                    - Closing Stock
                    {profitLossData.cogs_breakdown?.closing_stock_count > 0 && (
                      <span style={{ fontSize: '11px', color: '#888', marginLeft: '6px' }}>
                        ({profitLossData.cogs_breakdown.closing_stock_count} tags)
                      </span>
                    )}
                  </td>
                  <td className="item-amount">
                    ({formatCurrency(profitLossData.cogs_breakdown?.closing_stock || 0)})
                  </td>
                </tr>
                <tr className="total-row">
                  <td className="total-label"><strong>Cost of Goods Sold</strong></td>
                  <td className="total-amount">
                    <strong>{formatCurrency(profitLossData.cost_of_goods_sold || 0)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ==================== 3. GROSS PROFIT ==================== */}
          <div className="pl-section highlight-section">
            <h4 className="section-title gross-title">Gross Profit</h4>
            <table className="pl-table">
              <tbody>
                <tr className="gross-profit-row">
                  <td className="total-label">
                    <strong>Gross Profit (Sales Revenue − COGS)</strong>
                  </td>
                  <td className={`total-amount ${(profitLossData.gross_profit || 0) >= 0 ? 'profit-positive' : 'profit-negative'}`}>
                    <strong>{formatCurrency(profitLossData.gross_profit || 0)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ==================== 4. OPERATING EXPENSES ==================== */}
          <div className="pl-section">
            <h4 className="section-title expense-title">Operating Expenses</h4>
            <table className="pl-table">
              <tbody>
                {profitLossData.expenses?.details && profitLossData.expenses.details.length > 0 ? (
                  profitLossData.expenses.details.map((item, index) => (
                    <tr key={index}>
                      <td className="item-name">{item.account_name}</td>
                      <td className="item-amount">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="item-name">No Operating Expenses recorded</td>
                    <td className="item-amount">₹0.00</td>
                  </tr>
                )}
                <tr className="total-row">
                  <td className="total-label"><strong>Total Operating Expenses</strong></td>
                  <td className="total-amount">
                    <strong>{formatCurrency(profitLossData.expenses?.total || 0)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ==================== 5. NET PROFIT / LOSS ==================== */}
          <div className="pl-section net-profit-section">
            <h4 className="section-title net-title">Net Profit / Loss</h4>
            <table className="pl-table">
              <tbody>
                <tr className="net-result-row">
                  <td className="total-label">
                    <strong>
                      {profitLossData.net_profit >= 0 ? 'Net Profit' : 'Net Loss'}
                      {' '}(Gross Profit − Operating Expenses)
                    </strong>
                  </td>
                  <td className={`total-amount ${profitLossData.net_profit >= 0 ? 'profit-positive' : 'profit-negative'}`}>
                    <strong>{formatCurrency(Math.abs(profitLossData.net_profit || 0))}</strong>
                  </td>
                </tr>
                <tr>
                  <td className="item-name">Profit Percentage</td>
                  <td className={`item-amount ${profitLossData.profit_percentage >= 0 ? 'profit-positive' : 'profit-negative'}`}>
                    {profitLossData.profit_percentage || 0}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ==================== 6. SUMMARY CARDS ==================== */}
          <div className="pl-summary">
            <div className="summary-card">
              <span className="summary-label">Sales Revenue</span>
              <span className="summary-value">
                {formatCurrency(profitLossData.sales_revenue || profitLossData.income?.total || 0)}
              </span>
            </div>
            <div className="summary-card">
              <span className="summary-label">Cost of Goods Sold</span>
              <span className="summary-value">
                {formatCurrency(profitLossData.cost_of_goods_sold || 0)}
              </span>
            </div>
            <div className="summary-card">
              <span className="summary-label">Gross Profit</span>
              <span className={`summary-value ${(profitLossData.gross_profit || 0) >= 0 ? 'profit-positive' : 'profit-negative'}`}>
                {formatCurrency(profitLossData.gross_profit || 0)}
              </span>
            </div>
            <div className="summary-card">
              <span className="summary-label">Operating Expenses</span>
              <span className="summary-value">
                {formatCurrency(profitLossData.expenses?.total || 0)}
              </span>
            </div>
            <div className="summary-card highlight">
              <span className="summary-label">
                Net {profitLossData.net_profit >= 0 ? 'Profit' : 'Loss'}
              </span>
              <span className={`summary-value ${profitLossData.net_profit >= 0 ? 'profit-positive' : 'profit-negative'}`}>
                {formatCurrency(Math.abs(profitLossData.net_profit || 0))}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="pl-no-data">
          <span className="no-data-icon">📭</span>
          <p>No data available. Please generate the report.</p>
        </div>
      )}
    </div>
  );
};

export default ProfitAndLoss;